import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Button, Stack, Alert, Checkbox,
  FormControlLabel, Box,
} from "@mui/material";
import axios from "../../../config/axiosConfig";

const RAZONES = ["Vacación", "Permiso"];

// Días de calendario inclusive entre las dos fechas (span completo, sin
// descontar nada) — el ajuste por "medio día" ya NO se calcula acá, lo aplica
// directamente el checkbox (ver handleMedioDiaChange) sumando/restando 0.5
// sobre el valor que esté en el campo en ese momento.
const calcularDias = (fechaIni, fechaFin) => {
  if (!fechaIni || !fechaFin) return 0;
  const ini = new Date(`${fechaIni}T00:00:00`);
  const fin = new Date(`${fechaFin}T00:00:00`);
  const diff = Math.round((fin - ini) / 86400000) + 1;
  return diff > 0 ? diff : 0;
};

const initialForm = { razon: "", fecha_ini: "", fecha_fin: "", medioDia: false, dias: "", observacion: "" };

const EmitirSolicitudPermisoDialog = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Solo autocompleta "Días solicitados" cuando está VACÍO — una vez que
  // tiene algún valor (autocompletado o tipeado a mano), cambiar las fechas
  // ya no lo vuelve a pisar; el usuario lo ajusta él mismo (a mano o con el
  // checkbox de medio día, ver handleMedioDiaChange).
  useEffect(() => {
    setForm((prev) => {
      if (prev.dias !== "") return prev;
      const sugerido = calcularDias(prev.fecha_ini, prev.fecha_fin);
      return sugerido > 0 ? { ...prev, dias: String(sugerido) } : prev;
    });
  }, [form.fecha_ini, form.fecha_fin]);

  const dias = Number(form.dias) || 0;

  const rangoInvalido = form.fecha_ini && form.fecha_fin && form.fecha_fin < form.fecha_ini;

  const handleChange = (campo) => (e) => {
    setForm((prev) => ({ ...prev, [campo]: e.target.value }));
  };

  // El check no dispara ningún recálculo por fórmula: simplemente suma (al
  // tildar) o resta (al destildar) 0.5 sobre lo que ya esté en "Días
  // solicitados" — sea un valor autocompletado por fechas o tipeado a mano.
  const handleMedioDiaChange = (e) => {
    const checked = e.target.checked;
    setForm((prev) => {
      const actual = Number(prev.dias) || 0;
      const ajustado = checked ? actual + 0.5 : actual - 0.5;
      return { ...prev, medioDia: checked, dias: ajustado > 0 ? String(ajustado) : "" };
    });
  };

  const handleClose = () => {
    if (saving) return;
    setForm(initialForm);
    setError("");
    onClose();
  };

  const handleEnviar = async () => {
    setSaving(true);
    setError("");
    try {
      await axios.post("/api/permisos/solicitar", {
        razon: form.razon,
        fecha_ini: form.fecha_ini,
        fecha_fin: form.fecha_fin,
        hora_ini: "00:00:00",
        hora_fin: "00:00:00",
        observacion: form.observacion.trim(),
        medio_dia: form.medioDia,
        dias,
      });
      setForm(initialForm);
      onCreated();
    } catch (e) {
      setError(e?.response?.data?.message || "Error al emitir la solicitud.");
    } finally {
      setSaving(false);
    }
  };

  // dias >= 0 (0 incluido, a pedido) — el campo solo llega a "0" si el usuario
  // lo tipeó a mano (el autocompletado por rango de fechas nunca da 0, ver
  // calcularDias), así que sigue exigiendo que el campo tenga algo escrito.
  const puedeEnviar = form.razon && form.fecha_ini && form.fecha_fin && !rangoInvalido && form.dias !== "" && dias >= 0 && !saving;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>Nueva solicitud de permiso</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            select size="small" label="Razón" required
            value={form.razon} onChange={handleChange("razon")}
          >
            {RAZONES.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
          </TextField>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
            <TextField
              size="small" type="date" label="Fecha inicio" required
              InputLabelProps={{ shrink: true }}
              value={form.fecha_ini} onChange={handleChange("fecha_ini")}
            />
            <TextField
              size="small" type="date" label="Fecha fin" required
              InputLabelProps={{ shrink: true }}
              value={form.fecha_fin} onChange={handleChange("fecha_fin")}
              error={Boolean(rangoInvalido)}
              helperText={rangoInvalido ? "Debe ser posterior a la fecha inicio" : " "}
            />
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, alignItems: "start" }}>
            <TextField
              size="small" type="number" label="Días solicitados" required
              value={form.dias} onChange={handleChange("dias")}
              inputProps={{ min: 0, step: 0.5 }}
              helperText={`Total de días solicitados: ${dias}`}
            />
            <FormControlLabel
              control={<Checkbox checked={form.medioDia} onChange={handleMedioDiaChange} />}
              label="El último día es medio día"
              sx={{ mt: 0.5 }}
            />
          </Box>

          <TextField
            size="small" label="Observación" multiline minRows={3}
            value={form.observacion} onChange={handleChange("observacion")}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={handleEnviar} disabled={!puedeEnviar}>
          {saving ? "Enviando..." : "Enviar solicitud"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmitirSolicitudPermisoDialog;
